import React from 'react';
import { BoxArrowUp } from 'react-bootstrap-icons';
import { Dropdown } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Packer } from 'docx';
import { saveAs } from 'file-saver';
import { studentAPI } from '../services/api';

const ExportStudentsDropdown = ({ exporting, setExporting, showNotification }) => {

  const fetchExportData = async () => {
    try {
      const response = await studentAPI.getExportData();
      return response.data;
    } catch (error) {
      console.error('Error fetching export data:', error);
      showNotification('Error fetching export data', 'danger');
      return [];
    }
  };

  const exportToExcel = async () => {
    const exportData = await fetchExportData();
    if (!exportData || exportData.length === 0) return;

    const workbook = XLSX.utils.book_new();
    const formattedData = exportData.map((student, index) => ({
      'No.': index + 1,
      'Student Name': student.studentName || 'N/A',
      'Email': student.email || 'N/A',
      'Date of Birth': student.dateOfBirth
        ? XLSX.SSF.format('dd/mm/yyyy', new Date(student.dateOfBirth))
        : 'N/A',
      'Registration Date': student.registrationDate
        ? XLSX.SSF.format('dd/mm/yyyy', new Date(student.registrationDate))
        : 'N/A',
      'Gender': student.gender || 'N/A',
      'Last School': student.lastSchoolAttended || 'N/A',
      'Province': student.province || 'N/A',
      'Region': student.region || 'N/A',
      'Religion': student.religion || 'N/A',
      'District': student.district || 'N/A',
      'Constituency': student.constituency || 'N/A',
      'Village': student.village || 'N/A',
      'Class': student.class || 'N/A',
      'Phone': student.phoneNumber || 'N/A',
      'Father': student.fatherName || 'N/A',
      'Mother': student.motherName || 'N/A',
      "Father's Job": student.fathersOccupation || 'N/A',
      "Mother's Job": student.mothersOccupation || 'N/A',
      'Parents Phone': student.parentsPhoneNumber || 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    worksheet['!cols'] = [
      { wch: 5 }, { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 15 },
      { wch: 8 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 20 },
      { wch: 20 }, { wch: 8 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }, { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, `Students_List_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportToWord = async () => {
    const exportData = await fetchExportData();
    if (!exportData || exportData.length === 0) return;

    const { Document, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType } =
      await import('docx');

    function createHeaderCell(text) {
      return new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF' })], alignment: AlignmentType.CENTER })],
        margins: { top: 100, bottom: 100, left: 100, right: 100 },
        shading: { fill: '2B579A' },
      });
    }

    function createDataCell(text) {
      return new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text, size: 20 })], alignment: AlignmentType.CENTER })],
        margins: { top: 100, bottom: 100, left: 100, right: 100 },
      });
    }

    const doc = new Document({
      sections: [{
        properties: { page: { size: { orientation: 'LANDSCAPE', width: 15840, height: 12240 } } },
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Complete Students List', bold: true, size: 28 })],
            alignment: AlignmentType.CENTER,
          }),
          new Paragraph({
            children: [new TextRun({ text: `Exported on: ${new Date().toLocaleDateString()} | Total Students: ${exportData.length}` })],
            alignment: AlignmentType.CENTER,
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({
                tableHeader: true,
                children: [
                  'No.', 'Student Name', 'Email', 'Date of Birth', 'Registration Date',
                  'Gender', 'Last School', 'Province', 'Region', 'Religion',
                  'District', 'Constituency', 'Village', 'Class', 'Phone',
                  'Father', 'Mother', "Father's Occupation", "Mother's Occupation", 'Parents Phone',
                ].map(createHeaderCell),
              }),
              ...exportData.map((student, index) =>
                new TableRow({
                  children: [
                    (index + 1).toString(), student.studentName || 'N/A', student.email || 'N/A',
                    student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A',
                    student.registrationDate ? new Date(student.registrationDate).toLocaleDateString() : 'N/A',
                    student.gender || 'N/A', student.lastSchoolAttended || 'N/A',
                    student.province || 'N/A', student.region || 'N/A', student.religion || 'N/A',
                    student.district || 'N/A', student.constituency || 'N/A', student.village || 'N/A',
                    student.class || 'N/A', student.phoneNumber || 'N/A',
                    student.fatherName || 'N/A', student.motherName || 'N/A',
                    student.fathersOccupation || 'N/A', student.mothersOccupation || 'N/A',
                    student.parentsPhoneNumber || 'N/A',
                  ].map(createDataCell),
                })
              ),
            ],
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Students_List_${new Date().toISOString().slice(0, 10)}.docx`);
  };

  const exportToPDF = async () => {
    const exportData = await fetchExportData();
    if (!exportData || exportData.length === 0) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm' });
    const headers = [[
      'No.', 'Student Name', 'Email', 'DOB', 'Reg. Date', 'Gender', 'Last School',
      'Province', 'Region', 'Religion', 'District', 'Constituency', 'Village',
      'Class', 'Phone', 'Father', 'Mother', "Father's Occ.", "Mother's Occ.", 'Parents Phone',
    ]];

    const data = exportData.map((student, index) => [
      index + 1,
      student.studentName || 'N/A', student.email || 'N/A',
      student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A',
      student.registrationDate ? new Date(student.registrationDate).toLocaleDateString() : 'N/A',
      student.gender || 'N/A', student.lastSchoolAttended || 'N/A',
      student.province || 'N/A', student.region || 'N/A', student.religion || 'N/A',
      student.district || 'N/A', student.constituency || 'N/A', student.village || 'N/A',
      student.class || 'N/A', student.phoneNumber || 'N/A',
      student.fatherName || 'N/A', student.motherName || 'N/A',
      student.fathersOccupation || 'N/A', student.mothersOccupation || 'N/A',
      student.parentsPhoneNumber || 'N/A',
    ]);

    doc.setFontSize(16);
    doc.text('Complete Students List', 14, 15);
    doc.setFontSize(10);
    doc.text(`Exported on: ${new Date().toLocaleDateString()} | Total: ${exportData.length} students`, 14, 22);

    doc.autoTable({
      head: headers,
      body: data,
      startY: 30,
      styles: { fontSize: 6, cellPadding: 2, halign: 'center', valign: 'middle' },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', fontSize: 6 },
      columnStyles: {
        0: { cellWidth: 8 }, 1: { cellWidth: 25 }, 2: { cellWidth: 25 }, 3: { cellWidth: 12 },
        4: { cellWidth: 12 }, 5: { cellWidth: 10 }, 6: { cellWidth: 20 }, 7: { cellWidth: 15 },
        8: { cellWidth: 15 }, 9: { cellWidth: 18 }, 10: { cellWidth: 18 }, 11: { cellWidth: 8 },
        12: { cellWidth: 12 }, 13: { cellWidth: 15 }, 14: { cellWidth: 15 }, 15: { cellWidth: 15 },
        16: { cellWidth: 15 }, 17: { cellWidth: 15 },
      },
    });

    doc.save(`Students_List_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === 'excel') await exportToExcel();
      else if (format === 'word') await exportToWord();
      else if (format === 'pdf') await exportToPDF();
      showNotification(`Exported to ${format.toUpperCase()} successfully`, 'success');
    } catch (error) {
      showNotification(`Export failed: ${error.message}`, 'danger');
    } finally {
      setExporting(false);
    }
  };

const [open, setOpen] = React.useState(false);
const dropdownRef = React.useRef(null);

React.useEffect(() => {
  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

return (
  <div className="exp-dropdown" ref={dropdownRef}>
    <button
      className="exp-toggle"
      onClick={() => setOpen(!open)}
      disabled={exporting}
    >
      <BoxArrowUp />
      {exporting ? 'Exporting...' : 'Export'}
    </button>
    {open && (
      <div className="exp-menu">
        <button className="exp-item" onClick={() => { handleExport('excel'); setOpen(false); }} disabled={exporting}>
          <i className="bi bi-file-earmark-excel"></i> Excel
        </button>
        <button className="exp-item" onClick={() => { handleExport('word'); setOpen(false); }} disabled={exporting}>
          <i className="bi bi-file-earmark-word"></i> Word
        </button>
        <button className="exp-item" onClick={() => { handleExport('pdf'); setOpen(false); }} disabled={exporting}>
          <i className="bi bi-file-earmark-pdf"></i> PDF
        </button>
      </div>
    )}
  </div>
);
};

export default ExportStudentsDropdown;